/** Tarjeta movil con variantes, metadatos y avatar (design.md §22.1, §22.7). */
import { clsx } from 'clsx';
import type { KeyboardEvent, ReactNode } from 'react';
import { MobileIcon, type MobileIconName } from './MobileIcon';

type CardVariant = 'default' | 'interactive' | 'compact';

type MobileCardProps = {
  variant?: CardVariant;
  title: string;
  subtitle?: string;
  description?: string;
  icon?: MobileIconName;
  iconColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  avatar?: string;
  badge?: string | number;
  badgeVariant?: 'primary' | 'success' | 'warning' | 'error' | 'neutral';
  metadata?: Array<{ label: string; value: string | number }>;
  rightContent?: ReactNode;
  onClick?: () => void;
  children?: ReactNode;
  className?: string;
};

const iconColorStyles = {
  primary: 'bg-primary-light text-primary',
  secondary: 'bg-secondary-light text-secondary-dark-text',
  success: 'bg-success-light text-success-dark',
  warning: 'bg-warning-light text-warning-dark',
  error: 'bg-error-light text-error-dark',
  info: 'bg-info-light text-info-dark',
};

const badgeColorStyles = {
  primary: 'bg-primary text-white',
  success: 'bg-success-light text-success-dark',
  warning: 'bg-warning-light text-warning-dark',
  error: 'bg-error-light text-error-dark',
  neutral: 'bg-bg-tertiary text-text-secondary',
};

export function MobileCard({
  variant = 'default',
  title,
  subtitle,
  description,
  icon,
  iconColor = 'primary',
  avatar,
  badge,
  badgeVariant = 'primary',
  metadata,
  rightContent,
  onClick,
  children,
  className,
}: MobileCardProps) {
  const isClickable = onClick !== undefined;
  const isInteractive = variant === 'interactive' || isClickable;
  const isCompact = variant === 'compact';

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  }

  return (
    <div
      className={clsx(
        // Base v2: radio-card, sin borde y sombra suave
        'rounded-card bg-white overflow-hidden shadow-card',
        
        // Interactivo
        isInteractive && [
          'cursor-pointer transition-all duration-200',
          'hover:shadow-md hover:-translate-y-0.5',
          'active:scale-[0.98] active:shadow-sm',
        ],

        // Operable por teclado cuando actua como boton
        isClickable && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        isClickable && 'min-h-[44px]',
        
        // Custom
        className,
      )}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? title : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      <div className={clsx('flex items-center gap-3', !isCompact && 'p-4', isCompact && 'p-3')}>
        {/* Avatar o Icono */}
        {avatar ? (
          <div className="h-12 w-12 rounded-full overflow-hidden flex-shrink-0">
            <img src={avatar} alt="" className="h-full w-full object-cover" />
          </div>
        ) : icon ? (
          <div
            className={clsx(
              'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg',
              iconColorStyles[iconColor],
            )}
          >
            <MobileIcon name={icon} className="h-6 w-6" />
          </div>
        ) : null}

        {/* Contenido principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3
              title={title}
              className={clsx(
                'font-semibold truncate text-text-primary',
                isCompact && 'text-sm',
              )}
            >
              {title}
            </h3>
            {badge !== undefined && (
              <span
                className={clsx(
                  'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold',
                  badgeColorStyles[badgeVariant],
                )}
              >
                {badge}
              </span>
            )}
          </div>
          
          {subtitle && (
            <p title={subtitle} className="mt-0.5 truncate text-sm text-text-secondary">
              {subtitle}
            </p>
          )}
          
          {description && (
            <p className="text-sm text-text-tertiary mt-1 line-clamp-2">
              {description}
            </p>
          )}

          {/* Metadata */}
          {metadata && metadata.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              {metadata.map((item, idx) => (
                <div key={idx} className="flex min-w-0 items-center gap-1 text-xs">
                  <span className="shrink-0 text-text-tertiary">{item.label}:</span>
                  <span title={String(item.value)} className="min-w-0 flex-1 break-words font-medium text-text-secondary">{item.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contenido derecho */}
        {rightContent && (
          <div className="flex-shrink-0">{rightContent}</div>
        )}
        
        {/* Chevron para interactivos */}
        {isInteractive && !rightContent && (
          <MobileIcon name="chevron_right" className="h-5 w-5 shrink-0 text-text-tertiary" />
        )}
      </div>

      {/* Children adicional */}
      {children && (
        <div className="px-4 pb-4 pt-0 border-t border-border-light">{children}</div>
      )}
    </div>
  );
}

/** Card para el inicio con accion rapida. */
type HomeCardProps = {
  title: string;
  description: string;
  icon: MobileIconName;
  iconColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  badge?: string | number;
  onClick?: () => void;
};

export function HomeCard({
  title,
  description,
  icon,
  iconColor = 'primary',
  badge,
  onClick,
}: HomeCardProps) {
  return (
    <MobileCard
      variant="interactive"
      title={title}
      description={description}
      icon={icon}
      iconColor={iconColor}
      badge={badge}
      badgeVariant="error"
      onClick={onClick}
      rightContent={null}
    />
  );
}
